import React from 'react';
import { toast } from 'react-toastify';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eraser,
  Eye,
  FileSpreadsheet,
  FileText,
  Globe,
  Inbox,
  Link2,
  MapPin,
  MousePointerClick,
  Plus,
  Route as RouteIcon,
  Search,
  Trash2,
  X
} from 'lucide-react';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import EmptyState from '../ui/EmptyState';
import { Input } from '../ui/Input';
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  TableScroller
} from '../ui/Table';
import Tooltip from '../ui/Tooltip';
import SortArrow from '../common/SortArrow';
import RouteStopsModal from './RouteStopsModal';
import { cn } from '../../lib/cn';
import { useAuth } from '../../hooks/useAuth';
import {
  DATA_PAGE_SIZE,
  formatDate,
  getRouteDisplayName,
  highlightMatch
} from '../../utils/routeManagementHelpers';
import {
  exportRoutesPdf,
  exportRoutesXlsx,
  exportStopsPdf,
  exportStopsXlsx,
  exportSelectedRoutesXlsx,
  exportSelectedRoutesKml,
  exportSelectedRoutesPdf
} from '../../utils/exporters';

export default function DataView({ controller }) {
  const [stopsRouteId, setStopsRouteId] = React.useState(null);
  const {
    allRoutesOnPageSelected,
    allStopsOnPageSelected,
    clearMapSelection,
    dataRoutesFilter,
    dataRoutesPage,
    dataRoutesPageItems,
    dataRoutesSort,
    dataRoutesTotalPages,
    dataStopsFilter,
    dataStopsPage,
    dataStopsPageItems,
    dataStopsSort,
    dataStopsTotalPages,
    dataSubTab,
    deletingSelection,
    deselectDataItems,
    filteredDataRoutes,
    filteredDataStops,
    handleBulkDeleteRoutes,
    handleBulkDeleteStops,
    handleDeleteRoute,
    handleDeleteStop,
    resolveStopMasterName,
    savedRoutes,
    selectDataItems,
    selectedDataRouteIds,
    selectedDataStopIds,
    selectedFilteredRoutesCount,
    selectedFilteredStopsCount,
    setDataRoutesFilter,
    setDataRoutesPage,
    setDataStopsFilter,
    setDataStopsPage,
    setDataSubTab,
    setSelectedDataRouteIds,
    setSelectedDataStopIds,
    stops,
    toggleDataSelection,
    toggleDataSort,
    viewingRoute,
    viewingRouteIds,
    viewRoutesOnMap,
    viewStopsOnMap,
    visibleStopIds
  } = controller;

  const { isEditor } = useAuth();
  const [exporting, setExporting] = React.useState(null);
  // Set only while zipping several route PDFs, so the button can count them off.
  const [pdfProgress, setPdfProgress] = React.useState(null);
  const totalLinks = savedRoutes.reduce((total, route) => total + route.stops.length, 0);
  const isStopsTab = dataSubTab === 'stops';
  const selectedCount = isStopsTab ? selectedDataStopIds.size : selectedDataRouteIds.size;
  const selectedRoutesFromTable = savedRoutes.filter((r) => selectedDataRouteIds.has(r._id));

  const mapLoadedStops = visibleStopIds.size;
  const mapLoadedRoutes = (viewingRoute ? 1 : 0) + (viewingRouteIds ? viewingRouteIds.size : 0);

  const onOpenSelection = () => {
    if (isStopsTab) {
      viewStopsOnMap([...selectedDataStopIds]);
    } else {
      viewRoutesOnMap(selectedRoutesFromTable);
    }
  };

  const runExport = async (format) => {
    setExporting(format);
    try {
      if (isStopsTab) {
        const rows = filteredDataStops;
        if (format === 'xlsx') await exportStopsXlsx(rows);
        else await exportStopsPdf(rows);
      } else {
        const rows = filteredDataRoutes;
        if (format === 'xlsx') await exportRoutesXlsx(rows, resolveStopMasterName);
        else await exportRoutesPdf(rows, resolveStopMasterName);
      }
      toast.success(`${format.toUpperCase()} ready`);
    } catch (error) {
      toast.error(`Export failed: ${error.message || error}`);
    } finally {
      setExporting(null);
    }
  };

  // One PDF per route (table of stops + plotted map). A single route
  // downloads on its own; several arrive zipped as separate documents.
  const runRoutesPdfExport = async (routes, filenamePrefix) => {
    setExporting('pdf');
    setPdfProgress(routes.length > 1 ? { done: 0, total: routes.length } : null);
    try {
      await exportSelectedRoutesPdf(
        routes,
        stops,
        resolveStopMasterName,
        filenamePrefix,
        (done, total) => {
          if (total > 1) setPdfProgress({ done, total });
        }
      );
      toast.success(
        routes.length > 1 ? `${routes.length} route PDFs zipped` : 'PDF ready'
      );
    } catch (error) {
      toast.error(`Export failed: ${error.message || error}`);
    } finally {
      setExporting(null);
      setPdfProgress(null);
    }
  };

  return (
    <div className="flex-1 min-w-0 min-h-0 px-6 lg:px-8 py-6 flex flex-col gap-5 overflow-auto">
      <div className="min-w-0">
        <p className="text-[10.5px] font-semibold tracking-[0.14em] uppercase text-brand-600">
          Operations data
        </p>
        <h1 className="text-[24px] font-semibold tracking-tight text-slate-900 mt-1">
          Stops and routes
        </h1>
        <p className="text-[13.5px] text-slate-500 mt-1 max-w-2xl">
          Search master records, review saved routes, and open only the items you want to inspect on the map.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        <KpiCard
          label="Master stops"
          value={stops.length}
          sublabel="records"
          icon={MapPin}
          tone="emerald"
        />
        <KpiCard
          label="Saved routes"
          value={savedRoutes.length}
          sublabel="published"
          icon={RouteIcon}
          tone="brand"
        />
        <KpiCard
          label="Stop links"
          value={totalLinks}
          sublabel="across routes"
          icon={Link2}
          tone="violet"
        />
        <KpiCard
          label="Selected now"
          value={selectedCount}
          sublabel="for action"
          icon={MousePointerClick}
          tone="brand"
          highlight
        />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <SubTab
          active={dataSubTab === 'stops'}
          count={stops.length}
          icon={MapPin}
          onClick={() => setDataSubTab('stops')}
          label="Stops"
        />
        <SubTab
          active={dataSubTab === 'routes'}
          count={savedRoutes.length}
          icon={RouteIcon}
          onClick={() => setDataSubTab('routes')}
          label="Routes"
        />

        <div className="ml-auto flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={FileSpreadsheet}
            loading={exporting === 'xlsx' || exporting === 'xlsx-selected'}
            onClick={async () => {
              if (isStopsTab) {
                await runExport('xlsx');
                return;
              }

              if (selectedCount > 0) {
                setExporting('xlsx-selected');
                try {
                  await exportSelectedRoutesXlsx(selectedRoutesFromTable, stops, resolveStopMasterName);
                  toast.success('Excel ready');
                } catch (error) {
                  toast.error(`Export failed: ${error.message || error}`);
                } finally {
                  setExporting(null);
                }
              } else {
                await runExport('xlsx');
              }
            }}
            disabled={(isStopsTab ? filteredDataStops.length : filteredDataRoutes.length) === 0}
          >
            Excel
          </Button>

          {!isStopsTab && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Globe}
              loading={exporting === 'kml' || exporting === 'kml-selected'}
              onClick={async () => {
                if (selectedCount > 0) {
                  setExporting('kml-selected');
                  try {
                    await exportSelectedRoutesKml(selectedRoutesFromTable, stops, resolveStopMasterName);
                    toast.success('KML ready');
                  } catch (error) {
                    toast.error(`Export failed: ${error.message || error}`);
                  } finally {
                    setExporting(null);
                  }
                } else {
                  setExporting('kml');
                  try {
                    await exportSelectedRoutesKml(filteredDataRoutes, stops, resolveStopMasterName, 'routes-all');
                    toast.success('KML ready');
                  } catch (error) {
                    toast.error(`Export failed: ${error.message || error}`);
                  } finally {
                    setExporting(null);
                  }
                }
              }}
              disabled={filteredDataRoutes.length === 0}
              title={
                selectedCount > 0
                  ? `KML for ${selectedCount} selected route${selectedCount === 1 ? '' : 's'}`
                  : 'KML for every filtered route'
              }
            >
              KML
            </Button>
          )}

          <Button
            variant="secondary"
            size="sm"
            leftIcon={FileText}
            loading={exporting === 'pdf'}
            onClick={() => {
              if (isStopsTab) return runExport('pdf');
              return selectedCount > 0
                ? runRoutesPdfExport(selectedRoutesFromTable, 'routes')
                : runRoutesPdfExport(filteredDataRoutes, 'routes-all');
            }}
            disabled={(isStopsTab ? filteredDataStops.length : filteredDataRoutes.length) === 0}
            title={
              isStopsTab
                ? 'Stops table as PDF'
                : selectedCount > 0
                  ? `PDF for ${selectedCount} selected route${selectedCount === 1 ? '' : 's'}${selectedCount > 1 ? ' (zipped)' : ''}`
                  : 'PDF for every filtered route (zipped)'
            }
          >
            {pdfProgress ? `PDF ${pdfProgress.done}/${pdfProgress.total}` : 'PDF'}
          </Button>

          <Button
            variant="primary"
            size="sm"
            leftIcon={Eye}
            onClick={onOpenSelection}
            disabled={selectedCount === 0}
          >
            Open selection{selectedCount > 0 ? ` (${selectedCount})` : ''}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            leftIcon={Eraser}
            onClick={clearMapSelection}
            disabled={mapLoadedStops + mapLoadedRoutes === 0}
          >
            Reset map
          </Button>
        </div>
      </div>

      {isStopsTab && (
        <DataPanel
          placeholder="Search stops by name or Stop ID"
          filter={dataStopsFilter}
          onFilter={(v) => {
            setDataStopsFilter(v);
            setDataStopsPage(1);
          }}
          count={filteredDataStops.length}
          entity="stops"
          mapLoadedStops={mapLoadedStops}
          mapLoadedRoutes={mapLoadedRoutes}
          selectedCount={selectedDataStopIds.size}
          onSelectAll={() => selectDataItems('stops', filteredDataStops.map((s) => s._id))}
          onClearSelection={() => setSelectedDataStopIds(new Set())}
          onBulkDelete={handleBulkDeleteStops}
          deleting={deletingSelection}
          canEdit={isEditor}
          selectAllDisabled={
            filteredDataStops.length === 0 ||
            selectedFilteredStopsCount === filteredDataStops.length
          }
        >
          <TableScroller className="flex-1 min-h-0">
            <Table>
              <THead>
                <tr>
                  <TH className="w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      checked={allStopsOnPageSelected}
                      onChange={(e) =>
                        e.target.checked
                          ? selectDataItems('stops', dataStopsPageItems.map((s) => s._id))
                          : deselectDataItems('stops', dataStopsPageItems.map((s) => s._id))
                      }
                    />
                  </TH>
                  <TH className="w-10">#</TH>
                  <TH sortable onClick={() => toggleDataSort('stops', 'stopId')}>
                    Stop ID <SortArrow sort={dataStopsSort} col="stopId" />
                  </TH>
                  <TH sortable onClick={() => toggleDataSort('stops', 'stopName')}>
                    Name <SortArrow sort={dataStopsSort} col="stopName" />
                  </TH>
                  <TH sortable className="w-32" onClick={() => toggleDataSort('stops', 'lat')}>
                    Latitude <SortArrow sort={dataStopsSort} col="lat" />
                  </TH>
                  <TH sortable className="w-32" onClick={() => toggleDataSort('stops', 'lng')}>
                    Longitude <SortArrow sort={dataStopsSort} col="lng" />
                  </TH>
                  <TH sortable className="w-28" onClick={() => toggleDataSort('stops', 'createdAt')}>
                    Added <SortArrow sort={dataStopsSort} col="createdAt" />
                  </TH>
                  <TH className="w-24">Actions</TH>
                </tr>
              </THead>
              <TBody>
                {dataStopsPageItems.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="p-0">
                      <EmptyState
                        icon={Inbox}
                        title="No stops found"
                        description={
                          dataStopsFilter
                            ? `Nothing matched "${dataStopsFilter}".`
                            : 'Use the Stops page to add records one-by-one or import them in bulk.'
                        }
                        action={
                          !dataStopsFilter && isEditor ? (
                            <Button
                              size="sm"
                              variant="primary"
                              leftIcon={Plus}
                              onClick={() => controller.openCreateStopForm()}
                            >
                              Add a stop
                            </Button>
                          ) : null
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  dataStopsPageItems.map((stop, index) => (
                    <TR key={stop._id} selected={selectedDataStopIds.has(stop._id)}>
                      <TD>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                          checked={selectedDataStopIds.has(stop._id)}
                          onChange={() => toggleDataSelection('stops', stop._id)}
                        />
                      </TD>
                      <TD className="text-slate-400 tabular-nums text-[11.5px]">
                        {(dataStopsPage - 1) * DATA_PAGE_SIZE + index + 1}
                      </TD>
                      <TD className="font-mono text-[12.5px] text-slate-700">
                        {highlightMatch(stop.stopId || '', dataStopsFilter)}
                      </TD>
                      <TD className="font-medium text-slate-900 max-w-[260px] truncate">
                        {highlightMatch(stop.stopName, dataStopsFilter)}
                      </TD>
                      <TD className="font-mono text-[12.5px] text-slate-600">
                        {stop.lat.toFixed(6)}
                      </TD>
                      <TD className="font-mono text-[12.5px] text-slate-600">
                        {stop.lng.toFixed(6)}
                      </TD>
                      <TD className="text-slate-500 text-[12.5px]">
                        {formatDate(stop.createdAt)}
                      </TD>
                      <TD>
                        <div className="flex items-center gap-1">
                          <RowAction
                            tone="brand"
                            label="View on map"
                            icon={Eye}
                            onClick={() => viewStopsOnMap([stop._id])}
                          />
                          {isEditor && (
                            <RowAction
                              tone="rose"
                              label="Delete"
                              icon={Trash2}
                              onClick={() => handleDeleteStop(stop._id, stop.stopName)}
                            />
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </TableScroller>

          {dataStopsTotalPages > 1 && (
            <Pagination
              page={dataStopsPage}
              total={dataStopsTotalPages}
              onChange={setDataStopsPage}
            />
          )}
        </DataPanel>
      )}

      {!isStopsTab && (
        <DataPanel
          placeholder="Search by name, number or direction"
          filter={dataRoutesFilter}
          onFilter={(v) => {
            setDataRoutesFilter(v);
            setDataRoutesPage(1);
          }}
          count={filteredDataRoutes.length}
          entity="routes"
          mapLoadedStops={mapLoadedStops}
          mapLoadedRoutes={mapLoadedRoutes}
          selectedCount={selectedDataRouteIds.size}
          onSelectAll={() => selectDataItems('routes', filteredDataRoutes.map((r) => r._id))}
          onClearSelection={() => setSelectedDataRouteIds(new Set())}
          onBulkDelete={handleBulkDeleteRoutes}
          deleting={deletingSelection}
          canEdit={isEditor}
          selectAllDisabled={
            filteredDataRoutes.length === 0 ||
            selectedFilteredRoutesCount === filteredDataRoutes.length
          }
        >
          <TableScroller className="flex-1 min-h-0">
            <Table>
              <THead>
                <tr>
                  <TH className="w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                      checked={allRoutesOnPageSelected}
                      onChange={(e) =>
                        e.target.checked
                          ? selectDataItems('routes', dataRoutesPageItems.map((r) => r._id))
                          : deselectDataItems('routes', dataRoutesPageItems.map((r) => r._id))
                      }
                    />
                  </TH>
                  <TH className="w-10">#</TH>
                  <TH sortable onClick={() => toggleDataSort('routes', 'routeName')}>
                    Route name <SortArrow sort={dataRoutesSort} col="routeName" />
                  </TH>
                  <TH className="w-28">Route ID</TH>
                  <TH sortable className="w-20" onClick={() => toggleDataSort('routes', 'routeNumber')}>
                    No. <SortArrow sort={dataRoutesSort} col="routeNumber" />
                  </TH>
                  <TH sortable className="w-28" onClick={() => toggleDataSort('routes', 'direction')}>
                    Direction <SortArrow sort={dataRoutesSort} col="direction" />
                  </TH>
                  <TH sortable className="w-20" onClick={() => toggleDataSort('routes', 'stops')}>
                    Stops <SortArrow sort={dataRoutesSort} col="stops" />
                  </TH>
                  <TH sortable className="w-28" onClick={() => toggleDataSort('routes', 'createdAt')}>
                    Added <SortArrow sort={dataRoutesSort} col="createdAt" />
                  </TH>
                  <TH className="w-24">Actions</TH>
                </tr>
              </THead>
              <TBody>
                {dataRoutesPageItems.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="p-0">
                      <EmptyState
                        icon={Inbox}
                        title="No routes found"
                        description={
                          dataRoutesFilter
                            ? `Nothing matched "${dataRoutesFilter}".`
                            : 'Build your first route in the Routes page.'
                        }
                        action={
                          !dataRoutesFilter && isEditor ? (
                            <Button
                              size="sm"
                              variant="primary"
                              leftIcon={Plus}
                              onClick={() => controller.switchTab('routes')}
                            >
                              Create a route
                            </Button>
                          ) : null
                        }
                      />
                    </td>
                  </tr>
                ) : (
                  dataRoutesPageItems.map((route, index) => (
                    <TR key={route._id} selected={selectedDataRouteIds.has(route._id)}>
                      <TD>
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500 cursor-pointer"
                          checked={selectedDataRouteIds.has(route._id)}
                          onChange={() => toggleDataSelection('routes', route._id)}
                        />
                      </TD>
                      <TD className="text-slate-400 tabular-nums text-[11.5px]">
                        {(dataRoutesPage - 1) * DATA_PAGE_SIZE + index + 1}
                      </TD>
                      <TD className="font-medium text-slate-900 max-w-[280px] truncate">
                        {highlightMatch(
                          getRouteDisplayName(route, resolveStopMasterName),
                          dataRoutesFilter
                        )}
                      </TD>
                      <TD className="font-mono text-[11.5px] text-slate-500">
                        {route.routeId || '—'}
                      </TD>
                      <TD>
                        <Badge variant="brand" size="sm">{route.routeNumber}</Badge>
                      </TD>
                      <TD>
                        <Badge
                          variant={route.direction.toLowerCase().includes('up') ? 'success' : 'warning'}
                          size="sm"
                        >
                          {route.direction}
                        </Badge>
                      </TD>
                      <TD className="tabular-nums text-slate-600">{route.stops.length}</TD>
                      <TD className="text-slate-500 text-[12.5px]">{formatDate(route.createdAt)}</TD>
                      <TD>
                        <div className="flex items-center gap-1">
                          <RowAction
                            tone="brand"
                            label="View on map"
                            icon={Eye}
                            onClick={() => viewRoutesOnMap([route])}
                          />
                          <RowAction
                            tone="slate"
                            label="Route stops & codes"
                            icon={RouteIcon}
                            onClick={() => setStopsRouteId(route._id)}
                          />
                          {isEditor && (
                            <RowAction
                              tone="rose"
                              label="Delete"
                              icon={Trash2}
                              onClick={() => handleDeleteRoute(route._id, route.routeName)}
                            />
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          </TableScroller>

          {dataRoutesTotalPages > 1 && (
            <Pagination
              page={dataRoutesPage}
              total={dataRoutesTotalPages}
              onChange={setDataRoutesPage}
            />
          )}
        </DataPanel>
      )}

      <RouteStopsModal
        open={!!stopsRouteId}
        routeId={stopsRouteId}
        onClose={() => setStopsRouteId(null)}
      />
    </div>
  );
}

const TONE_STYLES = {
  brand:   { wrap: 'bg-brand-50 text-brand-600 border-brand-100' },
  emerald: { wrap: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  violet:  { wrap: 'bg-violet-50 text-violet-600 border-violet-100' },
  amber:   { wrap: 'bg-amber-50 text-amber-600 border-amber-100' }
};

function KpiCard({ label, value, sublabel, icon: Icon, tone = 'brand', highlight = false }) {
  const t = TONE_STYLES[tone] || TONE_STYLES.brand;
  return (
    <div
      className={cn(
        'flex-1 min-w-[200px] border rounded-xl p-4 shadow-soft hover:shadow-lift transition-shadow duration-150',
        highlight
          ? 'bg-gradient-to-br from-brand-50 via-white to-white border-brand-200'
          : 'bg-white border-slate-200'
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
        <span className={cn('h-7 w-7 rounded-md border flex items-center justify-center', t.wrap)}>
          {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5 mt-2">
        <p className="text-[28px] font-semibold text-slate-900 tabular-nums leading-none">
          {value}
        </p>
        {sublabel ? (
          <p className="text-[11.5px] text-slate-400">{sublabel}</p>
        ) : null}
      </div>
    </div>
  );
}

function SubTab({ active, count, icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-2 h-9 px-3.5 rounded-md text-[13px] font-semibold transition-all duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        active
          ? 'bg-brand-50 text-brand-700 border border-brand-100 shadow-soft'
          : 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300'
      )}
    >
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
      <span>{label}</span>
      <span
        className={cn(
          'tabular-nums text-[11px] font-semibold px-1.5 h-5 inline-flex items-center rounded-full',
          active ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'
        )}
      >
        {count}
      </span>
    </button>
  );
}

function DataPanel({
  placeholder,
  filter,
  onFilter,
  count,
  entity,
  mapLoadedStops,
  mapLoadedRoutes,
  selectedCount,
  onSelectAll,
  onClearSelection,
  onBulkDelete,
  deleting,
  canEdit,
  selectAllDisabled,
  children
}) {
  return (
    <div className="flex-1 min-h-0 flex flex-col bg-white border border-slate-200 rounded-xl shadow-soft overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100 bg-slate-50/40">
        <div className="flex-1 min-w-[240px] max-w-md">
          <Input
            leftIcon={Search}
            placeholder={placeholder}
            value={filter}
            onChange={(e) => onFilter(e.target.value)}
            rightSlot={
              filter ? (
                <button
                  type="button"
                  onClick={() => onFilter('')}
                  className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                >
                  <X className="h-3 w-3" />
                </button>
              ) : null
            }
          />
        </div>

        <div className="text-[12px] text-slate-500 whitespace-nowrap leading-tight">
          <p className="tabular-nums">
            {count} {entity}
            {selectedCount > 0 ? <span className="text-slate-400"> · {selectedCount} selected</span> : null}
          </p>
          <p className="text-[11.5px] text-slate-400 mt-0.5 tabular-nums">
            Map: {mapLoadedStops} stops · {mapLoadedRoutes} routes
          </p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={onSelectAll} disabled={selectAllDisabled}>
            Select filtered
          </Button>
          <Button variant="ghost" size="sm" onClick={onClearSelection} disabled={selectedCount === 0}>
            Clear selection
          </Button>
          {canEdit && (
            <Button
              variant="danger"
              size="sm"
              leftIcon={Trash2}
              loading={deleting}
              disabled={selectedCount === 0}
              onClick={onBulkDelete}
            >
              {deleting ? 'Deleting' : `Delete (${selectedCount})`}
            </Button>
          )}
        </div>
      </div>

      {children}
    </div>
  );
}

const ROW_TONES = {
  brand: 'text-slate-400 hover:bg-brand-50 hover:text-brand-700',
  rose:  'text-slate-400 hover:bg-rose-50 hover:text-rose-600',
  slate: 'text-slate-400 hover:bg-slate-100 hover:text-slate-700'
};

function RowAction({ icon: Icon, label, onClick, tone = 'brand' }) {
  return (
    <Tooltip label={label}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'h-7 w-7 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white transition-colors',
          ROW_TONES[tone]
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </button>
    </Tooltip>
  );
}

function Pagination({ page, total, onChange }) {
  const button =
    'h-8 w-8 inline-flex items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-30 disabled:hover:bg-white transition-colors';
  return (
    <div className="flex items-center justify-center gap-1.5 px-4 py-3 border-t border-slate-100 bg-white">
      <button type="button" className={button} disabled={page === 1} onClick={() => onChange(1)}>
        <ChevronsLeft className="h-3.5 w-3.5" />
      </button>
      <button type="button" className={button} disabled={page === 1} onClick={() => onChange(page - 1)}>
        <ChevronLeft className="h-3.5 w-3.5" />
      </button>
      <span className="text-[12.5px] text-slate-500 font-medium px-2 tabular-nums">
        Page {page} of {total}
      </span>
      <button type="button" className={button} disabled={page === total} onClick={() => onChange(page + 1)}>
        <ChevronRight className="h-3.5 w-3.5" />
      </button>
      <button type="button" className={button} disabled={page === total} onClick={() => onChange(total)}>
        <ChevronsRight className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
